import { isEmpty } from '@sgme/fp';
import type { ProductSelectorTabType } from 'components/shared/ProductAndClientSelector';
import { pick } from 'lodash-es';
import type { SelectedProductVariation } from 'models/productsReferential';
import type { IntlShape } from 'react-intl';
import { createSelector } from 'reselect';
import type { DashboardWorkspaceType } from 'state/quotes/infos/infos.model';
import {
  getAvailableProductVariations,
  getUserPreferencesProductVariation,
  getUserPreferencesSelectedOnBehalfClient,
} from 'state/selectors';
import type { AppState } from 'state/state.model';

import { isQuoteWorkspaceType, type QuoteWorkspaceType, type WorkspaceType } from '../model';
import { autoSelectProductVariation } from './workspaces.helpers';
import type { ActiveQuoteDashboard, WorkspacesStateMap, WorkspaceWithProductSelector } from './workspaces.model';

function getWorkspacesState(state: AppState, workspace: WorkspaceType) {
  return state.workspaces.workspaces[workspace];
}

export function getWorkspaceTabIds(state: AppState, workspace: WorkspaceType): string[] {
  return getWorkspacesState(state, workspace).tabs;
}

export function getWorkspaceActiveTabId(state: AppState, workspace: WorkspaceType) {
  return getWorkspacesState(state, workspace).activeTab as string;
}

function getWorkspaceStatus(state: AppState, workspace: WorkspaceType) {
  return getWorkspacesState(state, workspace).status;
}

export function isWorkspaceReady(state: AppState, workspace: WorkspaceType): boolean {
  return getWorkspaceStatus(state, workspace) === 'ready';
}

export function getWorkspaceDashboardVisibility(
  state: AppState,
  workspace: DashboardWorkspaceType,
): ActiveQuoteDashboard {
  return state.workspaces.workspaces[workspace].activeQuoteDashboard;
}

export const getWorkspaceTabModelWithWorkspaceType = (workspace: WorkspaceType) =>
  createSelector(
    [
      (state: AppState) => getWorkspaceTabIds(state, workspace),
      (state: AppState) => state.workspaces.tabs,
      (state: AppState) => getWorkspaceActiveTabId(state, workspace),
    ],
    (tabIds, tabs, activeTabId) => {
      const tabsModels = tabIds.map(id => tabs[id])?.filter(t => t?.id);
      const selectedTab = tabsModels.find(t => t.id === activeTabId) ?? null;
      return {
        tabs: tabsModels,
        selectedTab,
      };
    },
  );

export function getTabWorkspaceType(state: AppState, tabId: string) {
  const workspaces = state.workspaces?.workspaces ?? {};
  const workspaceType = Object.entries(workspaces).find(([, workspaceInfo]) => workspaceInfo.tabs.includes(tabId))?.[0];
  return (workspaceType ?? undefined) as WorkspaceType | undefined;
}

export function getTabQuoteWorkspaceType(state: AppState, tabId: string): QuoteWorkspaceType | undefined {
  const workspaceType = getTabWorkspaceType(state, tabId);
  return isQuoteWorkspaceType(workspaceType) ? workspaceType : undefined;
}

export function hasSeenProductSelectorDisclaimer(appState: AppState, workspace: WorkspaceWithProductSelector): boolean {
  return appState.workspaces.workspaces[workspace].hasSeenDisclaimer;
}

export function getSnapshotFromLocalStorage(appState: AppState): DeepPartial<WorkspacesStateMap> {
  return {
    newBatch: pick(appState.workspaces.workspaces.newBatch, ['hasSeenDisclaimer']),
    static: pick(appState.workspaces.workspaces.static, ['hasSeenDisclaimer']),
  };
}

export const getSelectedProductVariation = createSelector(
  (appState: AppState, workspace: WorkspaceWithProductSelector) => getAvailableProductVariations(appState, workspace),
  (appState: AppState, workspace: WorkspaceWithProductSelector) => getActivatedAssetTypeFilter(appState, workspace),
  (appState: AppState, workspace: WorkspaceWithProductSelector) =>
    appState.workspaces.workspaces[workspace].selectedProductVariation,
  (appState: AppState) => getUserPreferencesProductVariation(appState),
  (_: AppState, __: WorkspaceWithProductSelector, intl: IntlShape) => intl,
  (
    availableProductVariations,
    activatedAssetTypeFilter,
    selectedProductVariation,
    fromUserPreference,
    intl,
  ): Partial<SelectedProductVariation> => {
    const filteredProductVariations = availableProductVariations?.filter(
      ({ assetTypes }) =>
        assetTypes?.some(assetType => activatedAssetTypeFilter === assetType) || activatedAssetTypeFilter === 'All',
    );
    if (isEmpty(filteredProductVariations)) {
      return {};
    }

    if (selectedProductVariation) {
      return autoSelectProductVariation(selectedProductVariation, filteredProductVariations, intl);
    }

    if (fromUserPreference?.productType) {
      return autoSelectProductVariation(fromUserPreference, filteredProductVariations, intl);
    }

    return autoSelectProductVariation(
      { productType: filteredProductVariations[0].productType },
      filteredProductVariations,
      intl,
    );
  },
);

export function getSelectedClient(appState: AppState, workspace: WorkspaceWithProductSelector) {
  const { selectedClient } = appState.workspaces.workspaces[workspace];

  if (selectedClient || selectedClient === null) {
    return selectedClient;
  }

  return getUserPreferencesSelectedOnBehalfClient(appState) ?? null;
}

export function getSelectedProductSelectorTab(
  appState: AppState,
  workspace: WorkspaceWithProductSelector,
): ProductSelectorTabType {
  const { selectedProductSelectorTab } = appState.workspaces.workspaces[workspace];
  return selectedProductSelectorTab ?? 'catalogue';
}

export function getMostRecentProductsOpened(appState: AppState, workspace: WorkspaceWithProductSelector) {
  return appState.workspaces.workspaces[workspace].recentQuotedProductVariations ?? { status: 'error' };
}

export function selectMostRecentProductsOpenedPayload(appState: AppState, workspace: WorkspaceWithProductSelector) {
  const recent = appState.workspaces.workspaces[workspace].recentQuotedProductVariations;
  return recent?.status === 'success' ? (recent.payload ?? []) : [];
}

export function getActivatedAssetTypeFilter(appState: AppState, workspace: WorkspaceWithProductSelector): string {
  return appState.workspaces.workspaces[workspace].selectedAssetTypeFilter;
}

export function isSelectedProductVariationValid(
  appState: AppState,
  workspace: WorkspaceWithProductSelector,
  intl: IntlShape,
) {
  const { productType, productSubType, wrapper } = getSelectedProductVariation(appState, workspace, intl) ?? {};
  return Boolean(productType && productSubType && wrapper);
}

export function isWorkspaceSpaceImportCSVEnabled(state: AppState, workspace: WorkspaceType): boolean {
  const activeTabId = getWorkspaceActiveTabId(state, workspace);
  if (!activeTabId) {
    return false;
  }
  const tab = state.workspaces.tabs[activeTabId];
  return tab?.name === 'sumitomo';
}

export function getWorkspaceIsEssentialTabsPanelShown(state: AppState, workspace: WorkspaceType): boolean {
  if (workspace === 'essential') {
    return state.workspaces.workspaces['essential'].isTabsPanelShown;
  }
  return false;
}
